import { useState, useEffect, useMemo } from 'react'
import AggregateBox from '../../components/AggregateBox'
import { StackedBarChart } from '../../components/StackedBarChart'
import Map from '../../components/Map'
import { dashboardService } from '../../services/dashboard.service'
import Table from '../../components/Table'
import UserDetailPopup from '../../components/UserDetailPopup'
import {
  AdminDashboardDto,
  DASHBOARD_PERIODS,
  DASHBOARD_PERIOD_LABELS,
  DEFAULT_DASHBOARD_PERIOD,
  DashboardPeriod,
  dashboardPeriodToActivityPeriod,
  parseDashboardPeriod,
} from '@ivy-monorepo/shared'

const DashboardPage = () => {
  const [dashboard, setDashboard] = useState<AdminDashboardDto | undefined>()
  const [period, setPeriod] = useState<DashboardPeriod>(DEFAULT_DASHBOARD_PERIOD)
  const [selectedUser, setSelectedUser] = useState<string | null>(null)
  const [isUserPopupVisible, setIsUserPopupVisible] = useState(false)

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await dashboardService.getDashboard(period)
        setDashboard(response)
      } catch (err) {
        console.log('Failed to fetch dashboard', err)
      }
    }

    fetchDashboard()
  }, [period])

  useEffect(() => {
    if (selectedUser) {
      setIsUserPopupVisible(true)
    } else {
      const timer = setTimeout(() => {
        setIsUserPopupVisible(false)
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [selectedUser])

  const handleUserRowClick = (row: { publicId: string }) => {
    setSelectedUser(row.publicId)
  }

  const activityPeriod = useMemo(() => dashboardPeriodToActivityPeriod(period), [period])

  const withPeriod = (basePath: string) => {
    if (!activityPeriod) {
      return basePath
    }
    const separator = basePath.includes('?') ? '&' : '?'
    return `${basePath}${separator}period=${activityPeriod}`
  }

  const periodSuffix = dashboard?.periodLabel ? ` (${dashboard.periodLabel.toLowerCase()})` : ''

  return (
    <div>
      <div className="dashboard">
        <div className="dashboard-header">
          <h2>Dashboard</h2>
          <label className="dashboard-period-selector">
            <span>Time range</span>
            <select
              value={period}
              onChange={e => setPeriod(parseDashboardPeriod(e.target.value))}
            >
              {DASHBOARD_PERIODS.map(option => (
                <option key={option} value={option}>
                  {DASHBOARD_PERIOD_LABELS[option]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className={dashboard === undefined ? 'element-loading' : 'element-loaded'}>
          <div className="row no-gutter aggregates">
            <AggregateBox
              title={`Check-ins${periodSuffix}`}
              data={dashboard?.visits}
              to={withPeriod('/admin/venue-activity?view=visits')}
            />
            <AggregateBox title={`Active users${periodSuffix}`} data={dashboard?.activeUsers} to="/admin/users?status=Active" />
            <AggregateBox title={`Active venues${periodSuffix}`} data={dashboard?.activeVenues} to="/admin/venues?status=active" />
            <AggregateBox
              title={`Messages${periodSuffix}`}
              data={dashboard?.messages}
              to={withPeriod('/admin/user-interactions?view=conversations')}
            />
            <AggregateBox
              title={`Breadcrumbs${periodSuffix}`}
              data={dashboard?.breadcrumbs}
              to={withPeriod('/admin/venue-activity?view=breadcrumbs')}
            />
            <AggregateBox title={`Views${periodSuffix}`} data={dashboard?.recentViews} />
            <AggregateBox
              title={`RSVPs${periodSuffix}`}
              data={dashboard?.rsvps}
              to="/admin/venue-events?view=rsvps"
            />
            <AggregateBox
              title={`Visit plans${periodSuffix}`}
              data={dashboard?.visitPlans}
              to="/admin/venue-events?view=visit-plans"
            />
          </div>
          <div className="dashboard-chart">
            <span className="header">{`Monthly Visits`}</span>
            <StackedBarChart data={dashboard?.monthlyVisits || {}} title="Monthly Visits" />
          </div>
          <div className="row no-gutter">
            <div className="col-lg-6">
              <div className="dashboard-top-users">
                <span className="header">{`Top Users`}</span>
                <div className="frame">
                  <Table
                    id="users"
                    name="Users"
                    keyField="publicId"
                    initialSortColumn="visitCount"
                    initialSortOrder="desc"
                    columns={[
                      { label: '', accessor: 'imageUrl', type: 'userAvatar' },
                      { label: 'Name', accessor: 'lastName', type: 'fullNameAndTelephone' },
                      { label: 'Visits', accessor: 'visitCount' },
                      { label: 'Last active', accessor: 'lastActiveAt', type: 'datetime' },
                    ]}
                    sourceData={dashboard?.topUsers || []}
                    isPropertyBarVisible={false}
                    filterTerms=""
                    onRowClick={handleUserRowClick}
                  />
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="dashboard-map">
                <span className="header">{`Visits by state`}</span>
                <Map data={dashboard?.visitsByState} />
              </div>
            </div>
          </div>
        </div>
        <UserDetailPopup userPublicId={selectedUser || ''} isOpen={isUserPopupVisible} onClose={() => setSelectedUser(null)} />
      </div>
    </div>
  )
}

export default DashboardPage
